// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { useLocalMessage } from '@Helpers/';
import { OrgForm, OrgValuesForm } from '@Organisms/Organisation/OrgCreate/OrgForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';

interface OrgCreateProps {
    onCreate?: (Org: OrgValuesForm) => void;
    triggerElem?: ReactElement;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

export const OrgCreate = ({ onCreate, triggerElem, visible, onVisibleChange }: OrgCreateProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinish = (values: OrgValuesForm): void => {
        const { name = '', description = '', taskRule, observableRule } = values;
        if (onCreate) {
            onCreate({ name: name.trim(), description: description.trim(), taskRule, observableRule });
        }
    };

    return (
        <THDrawer
            title={i('organisation.create.drawer.title')}
            triggerElem={triggerElem}
            afterVisibleChange={(visible): void => {
                form.resetFields();
                onVisibleChange?.(visible);
            }}
            className="drawer-form"
            disableClose={fieldsTouched}
            visible={visible}
        >
            {(toggleDrawer) => (
                <OrgForm
                    isValid={isValid && fieldsTouched}
                    onFinish={(values) => {
                        onFinish(values);
                        toggleDrawer();
                    }}
                    form={form}
                    onCancel={() => {
                        form.resetFields();
                        setFieldsTouched(false);
                        toggleDrawer();
                    }}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                />
            )}
        </THDrawer>
    );
};
